/**
 * Which game sent this person into a course (Aran, job #513, 2026-10-08).
 *
 * Every link from a game into the app carries ?source=<game>-<language>
 * (gameSourceLink below). The first time any route is reached with one, this
 * browser remembers it — first touch, for 30 days — and the first time the
 * browser is signed in (the sign-up, or the sign-in that lets them enrol) it is
 * handed to /api/games/attribution, which stores it against the learner in
 * learner_game_attributions. RECORDING ONLY; nothing here changes what anybody
 * sees.
 */
import { watch, type Ref } from 'vue'
import { eventBody, sendWebEvent } from '@/composables/useWebActivity'
import { countingOff } from '@/composables/countingConsent'

const KEY = 'ssi-game-source'
const WINDOW_MS = 30 * 86_400_000
const SOURCE_RE = /^[a-z0-9_]+(-[a-z0-9_]+)*$/

interface Pending {
  source: string
  landedAt: string
  landingPath: string
}

/** The link a game puts on its "go and learn" button. */
export function gameSourceLink(source: string, path = '/'): string {
  const hashAt = path.indexOf('#')
  const base = hashAt < 0 ? path : path.slice(0, hashAt)
  const hash = hashAt < 0 ? '' : path.slice(hashAt)
  return `${base}${base.includes('?') ? '&' : '?'}source=${encodeURIComponent(source)}${hash}`
}

function read(): Pending | null {
  try {
    const p = JSON.parse(localStorage.getItem(KEY) || 'null') as Pending | null
    if (!p) return null
    const landedAt = Date.parse(p.landedAt)
    if (!Number.isFinite(landedAt) || Date.now() - landedAt > WINDOW_MS) return null
    return p
  } catch {
    return null
  }
}

/** Called on every navigation: remember a ?source= the first time it is seen. */
export function captureGameSource(query: Record<string, unknown>, path: string): void {
  const source = query.source
  if (typeof source !== 'string' || !SOURCE_RE.test(source) || source.length > 40) return
  noteAppOpen(source, path)
  if (countingOff()) return // objected to counting: nothing remembered
  const existing = read()
  if (existing && existing.source === source) return
  if (existing) return // first touch wins inside the window
  try {
    localStorage.setItem(KEY, JSON.stringify({ source, landedAt: new Date().toISOString(), landingPath: path.slice(0, 80) }))
  } catch {
    /* private mode — nothing to remember with */
  }
  // Already signed in (a player tapping "start learning" from the game): claim now.
  if (claimer?.isAuthenticated.value) void claimGameSource(claimer.getToken)
}

const opened = new Set<string>()
/**
 * The app was reached from a game (job #841): one anonymous web event, once per source per page
 * load, so the Business page's game funnel can count "opened the app". It carries the counting id
 * through eventBody, and sends nothing from a device that objected (countingConsent.ts).
 */
function noteAppOpen(source: string, path: string): void {
  if (opened.has(source)) return
  opened.add(source)
  sendWebEvent(eventBody('app_open', { source, path }))
}
/** Test seam. */
export function resetAppOpens(): void {
  opened.clear()
}

let claimer: { isAuthenticated: Ref<boolean>; getToken: () => Promise<string | null> } | null = null

let claiming = false
/** Hand a remembered source to the server once this browser is signed in. */
export async function claimGameSource(getToken: () => Promise<string | null>): Promise<void> {
  const p = read()
  if (!p || claiming || countingOff()) return
  claiming = true
  try {
    const stored = localStorage.getItem(KEY)
    const token = await getToken()
    if (!token) return
    const resp = await fetch('/api/games/attribution', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(p),
    }).catch(() => null)
    // Stored, or refused as not an attribution: either way it is done with.
    // An expired source can be replaced while token lookup or the request waits.
    if (resp && (resp.ok || resp.status === 400) && localStorage.getItem(KEY) === stored) {
      localStorage.removeItem(KEY)
    }
  } catch {
    // Called fire-and-forget: auth or storage failures must not reject into
    // navigation. Leave the source available for a later claim.
  } finally {
    claiming = false
  }
}

/** App-level: claim whenever the browser becomes signed in. */
export function watchGameAttribution(isAuthenticated: Ref<boolean>, getToken: () => Promise<string | null>): void {
  claimer = { isAuthenticated, getToken }
  watch(
    isAuthenticated,
    (yes) => {
      if (yes) void claimGameSource(getToken)
    },
    { immediate: true },
  )
}
